import { useEffect, useState } from 'react';
import type { PublicShareModel } from './publicShareModel';

export type PublicSharePort = {
  read(
    token: string,
    signal: AbortSignal,
    customerVersionNo?: number,
  ): Promise<PublicShareModel>;
};
type PublicShareState =
  | { status: 'loading'; token: string }
  | { status: 'ready'; token: string; value: PublicShareModel }
  | { status: 'error'; token: string; message: string };

export function usePublicShare(
  port: PublicSharePort,
  token: string,
  customerVersionNo?: number,
) {
  const [state, setState] = useState<PublicShareState>({
    status: 'loading',
    token,
  });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    setState({ status: 'loading', token });
    void port
      .read(token, controller.signal, customerVersionNo)
      .then((value) => {
        if (controller.signal.aborted) return;
        const expire = () => {
          if (controller.signal.aborted) return;
          const remaining = Date.parse(value.effectiveExpiresAt) - Date.now();
          if (remaining <= 0)
            setState({ status: 'error', token, message: '分享链接已过期' });
          else timer = setTimeout(expire, Math.min(remaining, 2_147_000_000));
        };
        if (Date.parse(value.effectiveExpiresAt) <= Date.now()) {
          expire();
          return;
        }
        setState({ status: 'ready', token, value });
        expire();
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setState({
            status: 'error',
            token,
            message: '分享链接不可用、已失效或服务暂不可用，请重试。',
          });
      });
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [customerVersionNo, port, token, revision]);
  return {
    state:
      state.token === token ? state : { status: 'loading' as const, token },
    retry: () => {
      setState({ status: 'loading', token });
      setRevision((value) => value + 1);
    },
  };
}
